import { Search, Sparkles, X } from "lucide-react"
import React, { useCallback, useEffect, useRef, useState } from "react"
import ReactDOM from 'react-dom/client'
import PackagingGuide from "./components/packaging-guide"
import ProcessingGuide from "./components/processing-guide"
import { ThemeProvider } from "./components/theme-provider"
import { Button } from "./components/ui/button"
import { Input } from "./components/ui/input"
import { DEFAULT_API_ENDPOINT } from "./lib/default-api-endpoint"
import { PackagingIcon, ProcessingIcon } from "./lib/icons"
import { themeConfig } from "./lib/theme-config"
import type { ProcessingData } from "./lib/types"
import { tailwindStyles } from './styles/tailwind-inline'

const getUrlParams = () => {
    try {
        const urlParams = new URLSearchParams(window.location.search)
        const search = urlParams.get('search') || ''
        const tab = (urlParams.get('tab') as 'processing' | 'packaging') || 'processing'
        const packagingType = urlParams.get('packagingType') || ''
        return { search, tab, packagingType }
    } catch (error) {
        console.error('Error reading URL params:', error)
        return { search: '', tab: 'processing' as const, packagingType: '' }
    }
}

const updateUrlParams = (
    search: string,
    tab: 'processing' | 'packaging',
    packagingType?: string,
    options?: { omitSearch?: boolean },
) => {
    const url = new URL(window.location.href)
    if (!options?.omitSearch) {
        if (search) {
            url.searchParams.set('search', search)
        } else {
            url.searchParams.delete('search')
        }
    }
    url.searchParams.set('tab', tab)

    if (packagingType) {
        url.searchParams.set('packagingType', packagingType)
    } else {
        url.searchParams.delete('packagingType')
    }

    window.history.replaceState({}, '', url.toString())
}

export { ProcessingIcon, PackagingIcon }

interface PackageType {
    package_type: string
    id: string
    package_category: string
}

export type EmbeddedSearchTrigger = {
    requestId: number
    query: string
    image?: File | null
}

export interface StandaloneComponentProps {
    initialQuery?: string
    /** When omitted, defaults to `DEFAULT_API_ENDPOINT` (ai-classifier host). */
    apiEndpoint?: string
    theme?: 'light' | 'dark' | 'system'
    containerClassName?: string
    onSearch?: (query: string) => void
    onGuideGenerated?: (data: ProcessingData) => void
    /** Use `embedded` when an omni host owns the search bar and drives searches via `embeddedSearchTrigger`. */
    variant?: 'standalone' | 'embedded'
    /** When the host submits a search, bump `requestId` and set `query` to run the guide bootstrap + render guides. */
    embeddedSearchTrigger?: EmbeddedSearchTrigger
    /** When true, use prosourceUrl instead of url for equipment category links. */
    useProsourceUrls?: boolean
}

type ManufacturingGuideInnerProps = StandaloneComponentProps & {
    apiEndpoint: string
    theme: 'light' | 'dark' | 'system'
    variant: 'standalone' | 'embedded'
    useProsourceUrls: boolean
}

function ManufacturingGuideInner({
    initialQuery = '',
    apiEndpoint,
    theme = 'light',
    variant,
    embeddedSearchTrigger,
    onSearch,
    onGuideGenerated,
    useProsourceUrls = false,
}: ManufacturingGuideInnerProps) {
    const [searchQuery, setSearchQuery] = useState('')
    const [activeTab, setActiveTab] = useState<"processing" | "packaging">("processing")
    const [suggestedTerms, setSuggestedTerms] = useState<string[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [hasSearched, setHasSearched] = useState(false)
    const [packagingTypes, setPackagingTypes] = useState<PackageType[]>([])
    const [allPackageTypes, setAllPackageTypes] = useState<PackageType[]>([])
    const [selectedPackagingType, setSelectedPackagingType] = useState<string>('')
    const [isInitialized, setIsInitialized] = useState(false)
    const [searchQueryFromUrl, setSearchQueryFromUrl] = useState(false)
    const [uploadedImage, setUploadedImage] = useState<File | null>(null)

    const embedded = variant === 'embedded'

    useEffect(() => {
        const urlParams = getUrlParams()
        if (embedded) {
            setSearchQuery('')
            setActiveTab(urlParams.tab)
            setSelectedPackagingType(urlParams.packagingType)
            setSearchQueryFromUrl(false)
            setIsInitialized(true)
            setIsLoading(false)
            return
        }
        const hasUrlSearch = urlParams.search || initialQuery
        setSearchQuery(hasUrlSearch)
        setActiveTab(urlParams.tab)
        setSelectedPackagingType(urlParams.packagingType)
        setSearchQueryFromUrl(!!hasUrlSearch)
        setIsInitialized(true)
    }, [embedded, initialQuery])

    useEffect(() => {
        if (embedded) return
        updateUrlParams(searchQuery, activeTab, selectedPackagingType)
    }, [searchQuery, activeTab, selectedPackagingType, embedded])

    useEffect(() => {
        if (embedded) return
        const loadSuggestions = async () => {
            try {
                const terms = [
                    "Chocolate Chip Cookies",
                    "Frozen Peas",
                    "Beef Jerky",
                    "Candles",
                    "Soda",
                    "Wine",
                    "Toothpaste",
                    "Lipstick",
                    "Soap",
                    "Tea",
                    "Coffee",
                ]
                setSuggestedTerms(terms)
            } catch (error) {
                console.error("Failed to load suggested search terms:", error)
                setSuggestedTerms([])
            } finally {
                setIsLoading(false)
            }
        }

        loadSuggestions()
    }, [embedded])

    const performSearch = useCallback(async (query: string, image?: File | null) => {
        setHasSearched(true)
        setUploadedImage(image ?? null)

        try {
            if (onSearch) {
                onSearch(query.trim())
            }

            const classifierFormData = new FormData()
            classifierFormData.append('query', query.trim())
            if (image) {
                classifierFormData.append('image', image, image.name)
                classifierFormData.append('image_type', image.type)
                classifierFormData.append('image_last_modified', image.lastModified.toString())
            }

            const [classifierResponse, packageTypesResponse] = await Promise.all([
                fetch(`${apiEndpoint}/api/package-type-classifier`, {
                    method: 'POST',
                    body: classifierFormData,
                }),
                fetch(`${apiEndpoint}/api/package-types`, {
                    method: 'GET',
                })
            ])

            if (!classifierResponse.ok) {
                throw new Error('Package type classifier API call failed')
            }

            if (!packageTypesResponse.ok) {
                throw new Error('Package types API call failed')
            }

            const [packagingTypesData, allPackageTypesData] = await Promise.all([
                classifierResponse.json(),
                packageTypesResponse.json()
            ])

            setPackagingTypes(packagingTypesData.slice(0, 4))
            setAllPackageTypes(allPackageTypesData)

            const guideData: ProcessingData = {
                name: query.trim(),
                packagingTypes: packagingTypesData.slice(0, 4),
            }

            if (onGuideGenerated) {
                onGuideGenerated(guideData)
            }

            console.log('Guide generated:', guideData)

        } catch (error) {
            console.error("Error generating guide:", error)
        }
    }, [apiEndpoint, onGuideGenerated, onSearch])

    useEffect(() => {
        if (!embedded || !embeddedSearchTrigger?.requestId) return
        const q = embeddedSearchTrigger.query.trim()
        if (!q) return
        setSearchQuery(q)
        setSearchQueryFromUrl(false)
        void performSearch(q, embeddedSearchTrigger.image ?? null)
    }, [embedded, embeddedSearchTrigger?.requestId, embeddedSearchTrigger?.query, embeddedSearchTrigger?.image, performSearch])

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!searchQuery.trim()) return
        await performSearch(searchQuery)
    }

    const handleSearchFromUrl = async (query: string) => {
        if (!query.trim()) return
        await performSearch(query)
    }

    useEffect(() => {
        if (embedded) return
        if (isInitialized && searchQuery && !hasSearched && searchQueryFromUrl) {
            handleSearchFromUrl(searchQuery)
        }
    }, [embedded, isInitialized, searchQuery, hasSearched, searchQueryFromUrl])

    useEffect(() => {
        if (embedded) return
        if (isInitialized && selectedPackagingType && packagingTypes.length > 0 && !hasSearched) {
            const foundType = packagingTypes.find(type => type.package_type === selectedPackagingType)
            if (foundType) {
                handlePackagingTypeSelect(selectedPackagingType)
            }
        }
    }, [embedded, isInitialized, selectedPackagingType, packagingTypes, hasSearched])

    const handleSuggestionClick = async (suggestion: string) => {
        setSearchQuery(suggestion)
        setSearchQueryFromUrl(false)
        await performSearch(suggestion)
    }

    const handleTabChange = (tab: "processing" | "packaging") => {
        setActiveTab(tab)
    }

    const handlePackagingTypeSelect = (packagingType: string) => {
        setSelectedPackagingType(packagingType)
    }

    const handleClear = () => {
        setSearchQuery('')
        setHasSearched(false)
        setPackagingTypes([])
        setSelectedPackagingType('')
        setActiveTab('processing')
        setSearchQueryFromUrl(false)
        if (!embedded) updateUrlParams('', 'processing', '')
    }

    return (
        <ThemeProvider
            attribute="class"
            defaultTheme={theme}
            enableSystem
            disableTransitionOnChange
        >
            <div className={embedded ? "w-full" : "flex flex-col items-center justify-center max-w-3xl mx-auto p-4"}>
                {!embedded ? (
                    <form onSubmit={handleSearch} className="flex flex-col sm:flex-row w-full mb-10 gap-4 sm:gap-0">
                        <div className="relative flex-grow">
                            <Input
                                type="text"
                                placeholder="Type what you want to manufacture"
                                className="pr-20 h-12"
                                style={{
                                    borderColor: themeConfig.colors.primary,
                                    backgroundColor: '#F0F5F2',
                                    color: themeConfig.colors.primary,
                                    fontWeight: 500,
                                    fontStyle: 'italic',
                                }}
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value)
                                    setSearchQueryFromUrl(false)
                                }}
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    className="absolute right-10 top-1/2 -translate-y-1/2 hover:bg-gray-200 rounded-full p-1 transition-colors"
                                >
                                    <X className="h-4 w-4 text-gray-500" />
                                </button>
                            )}
                            <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2">
                                <Search className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <Button
                            type="submit"
                            className="sm:ml-4 h-12 text-white text-base w-full sm:w-auto"
                            style={{ backgroundColor: themeConfig.colors.primary }}
                        >
                            GENERATE GUIDE
                        </Button>
                    </form>
                ) : null}

                {!embedded ? (
                    <div className="w-full max-w-xl">
                        {isLoading && !hasSearched && (
                            <p className="text-gray-700 mb-4">Loading suggestions...</p>
                        )}
                        {!isLoading && !hasSearched && (
                            <>
                                <p className="text-gray-700 mb-4">Or try one of these:</p>
                                <div className="flex flex-wrap gap-2">
                                    {suggestedTerms.map((item) => (
                                        <button
                                            key={item}
                                            onClick={() => handleSuggestionClick(item)}
                                            className="px-4 py-2 rounded-md hover:bg-opacity-80 transition-colors"
                                            style={{
                                                backgroundColor: `${themeConfig.colors.primary}10`,
                                                color: themeConfig.colors.primary,
                                            }}
                                        >
                                            {item}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                ) : null}

                {hasSearched && (
                    embedded ? (
                        <div className="w-full p-6 bg-white border border-gray-200 rounded-md shadow-sm">
                            <div className="flex items-center gap-2 mb-6">
                                <Sparkles className="h-5 w-5" style={{ color: '#00476B' }} />
                                <h3 className="text-xl font-semibold" style={{ color: '#00476B' }}>
                                    Are you looking for a <span className="italic">{searchQuery}</span> processing or packaging guide?
                                </h3>
                            </div>
                            <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
                                <div className="min-w-0">
                                    <ProcessingGuide
                                        productName={searchQuery}
                                        apiEndpoint={apiEndpoint}
                                        useProsourceUrls={useProsourceUrls}
                                        uploadedImage={uploadedImage}
                                        compact
                                    />
                                </div>
                                <div className="min-w-0 border-t border-gray-200 pt-6 md:border-t-0 md:pt-0 md:pl-6 md:border-l">
                                    <PackagingGuide
                                        productName={searchQuery}
                                        apiEndpoint={apiEndpoint}
                                        packagingTypes={packagingTypes}
                                        allPackageTypes={allPackageTypes}
                                        selectedPackagingType={selectedPackagingType}
                                        onPackagingTypeSelect={handlePackagingTypeSelect}
                                        compact
                                        useProsourceUrls={useProsourceUrls}
                                        uploadedImage={uploadedImage}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="w-full max-w-3xl">
                            <h2 className="text-xl font-bold mb-4 text-center" style={{ color: themeConfig.colors.primary }}>
                                Which guide would you like to generate?
                            </h2>

                            <div className="flex justify-center gap-6 mb-6">
                                <button
                                    className={`flex flex-col items-center justify-center rounded-xl shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary font-bold text-lg select-none ${activeTab === "processing" ? "bg-[#0A4D6E] text-white" : "bg-[#F5F8F7] text-[#0A4D6E]"}`}
                                    style={{ width: 184, height: 112 }}
                                    onClick={() => handleTabChange("processing")}
                                >
                                    <span className="mb-4">
                                        <ProcessingIcon color={activeTab === "processing" ? "white" : "#0A4D6E"} />
                                    </span>
                                    PROCESSING
                                </button>
                                <button
                                    className={`flex flex-col items-center justify-center rounded-xl shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary font-bold text-lg select-none ${activeTab === "packaging" ? "bg-[#0A4D6E] text-white" : "bg-[#F5F8F7] text-[#0A4D6E]"}`}
                                    style={{ width: 184, height: 112 }}
                                    onClick={() => handleTabChange("packaging")}
                                >
                                    <span className="mb-4">
                                        <PackagingIcon color={activeTab === "packaging" ? "white" : "#0A4D6E"} />
                                    </span>
                                    PACKAGING
                                </button>
                            </div>

                            {activeTab === "processing" && (
                                <ProcessingGuide
                                    productName={searchQuery}
                                    apiEndpoint={apiEndpoint}
                                    useProsourceUrls={useProsourceUrls}
                                    uploadedImage={uploadedImage}
                                />
                            )}
                            {activeTab === "packaging" && (
                                <PackagingGuide
                                    productName={searchQuery}
                                    apiEndpoint={apiEndpoint}
                                    packagingTypes={packagingTypes}
                                    allPackageTypes={allPackageTypes}
                                    selectedPackagingType={selectedPackagingType}
                                    onPackagingTypeSelect={handlePackagingTypeSelect}
                                    useProsourceUrls={useProsourceUrls}
                                    uploadedImage={uploadedImage}
                                />
                            )}
                        </div>
                    )
                )}
            </div>
        </ThemeProvider>
    )
}

export function StandaloneComponent({
    initialQuery = '',
    apiEndpoint = DEFAULT_API_ENDPOINT,
    theme = 'light',
    containerClassName = '',
    onSearch,
    onGuideGenerated,
    variant = 'standalone',
    embeddedSearchTrigger,
    useProsourceUrls = false,
}: StandaloneComponentProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const reactRootRef = useRef<ReactDOM.Root | null>(null)

    useEffect(() => {
        if (!containerRef.current || reactRootRef.current) return

        const shadowRoot = containerRef.current.attachShadow({ mode: 'open' })
        const shadowContainer = document.createElement('div')
        shadowContainer.id = 'shadow-container'
        shadowContainer.className = `manufacturing-guide-widget ${containerClassName}`
        shadowRoot.appendChild(shadowContainer)

        const styleElement = document.createElement('style')
        styleElement.textContent = tailwindStyles || ''
        shadowRoot.prepend(styleElement)

        reactRootRef.current = ReactDOM.createRoot(shadowContainer)

        return () => {
            reactRootRef.current?.unmount()
            reactRootRef.current = null
        }
    }, [])

    useEffect(() => {
        if (!reactRootRef.current) return
        reactRootRef.current.render(
            <ManufacturingGuideInner
                initialQuery={initialQuery}
                apiEndpoint={apiEndpoint}
                theme={theme}
                variant={variant}
                embeddedSearchTrigger={embeddedSearchTrigger}
                onSearch={onSearch}
                onGuideGenerated={onGuideGenerated}
                useProsourceUrls={useProsourceUrls}
            />
        )
    }, [
        initialQuery,
        apiEndpoint,
        theme,
        variant,
        embeddedSearchTrigger,
        onSearch,
        onGuideGenerated,
    ])

    return <div ref={containerRef} className={containerClassName} />
}
